import React from 'react'
import ReactDOM from 'react-dom/client'
import App from './App'
import './index.css'
import './styles/modal-fix.css'
import MaintenanceMode from './components/MaintenanceMode'
import { registerSW } from 'virtual:pwa-register'
import { syncService } from './services/syncService'
import { cacheService } from './services/cacheService'
// Import performance monitoring to initialize it
import { initializePerformanceMonitoring } from './utils/performanceMonitoring'
// Import production services
import { errorTrackingService } from './services/errorTracking'
import { analyticsService } from './services/analytics'
import { postLaunchMonitoringService } from './services/postLaunchMonitoring'
import { productionConfig, isProduction, validateProductionConfig, logProductionConfig } from './config/production'

// Register service worker with enhanced update handling
const updateSW = registerSW({
  onNeedRefresh() {
    // This will be handled by the UpdateNotification component
    console.log('New content available, please refresh.');
  },
  onOfflineReady() {
    console.log('App ready to work offline');
    
    // Initialize cache management when offline ready
    initializeCacheManagement();
  },
  onRegistered() {
    console.log('Service Worker registered successfully');
    
    // Schedule periodic cache cleanup
    cacheService.schedulePeriodicCleanup();
  },
  onRegisterError(error) {
    console.error('Service Worker registration failed:', error);
  }
});

// Initialize production services
function initializeProductionServices() {
  try {
    // Validate production configuration
    const validation = validateProductionConfig();
    if (!validation.valid) {
      console.error('Production configuration validation failed:', validation.errors);
      if (isProduction()) {
        // In production, log errors but don't break the app
        validation.errors.forEach(error => {
          errorTrackingService.reportError(`Configuration Error: ${error}`);
        });
      }
    }
    
    // Initialize error tracking
    errorTrackingService.initialize();
    
    // Initialize analytics
    analyticsService.initialize();
    
    // Initialize post-launch monitoring
    postLaunchMonitoringService.initialize();
    
    // Log production configuration (without sensitive data)
    if (isProduction()) {
      logProductionConfig();
      console.log('Production services initialized successfully');
    } else {
      console.log('Development mode: Production services initialized with local storage fallback');
    }
  } catch (error) {
    console.error('Failed to initialize production services:', error);
    // Don't let production service failures break the app
  }
}

// Initialize cache management
async function initializeCacheManagement() {
  try {
    // Check if cache migration is needed
    const needsMigration = await cacheService.needsCacheMigration('v1.0.0');
    if (needsMigration) {
      console.log('Performing cache migration...');
      await cacheService.migrateCaches('v1.0.0');
    }

    // Check cache health
    const isNearLimit = await cacheService.isCacheNearLimit(0.8);
    if (isNearLimit) {
      console.log('Cache near limit, performing cleanup...');
      await cacheService.cleanupCaches();
    }

    // Get cache status for debugging
    const status = await cacheService.getCacheStatus();
    console.log('Cache status:', status);
  } catch (error) {
    console.warn('Cache management initialization failed:', error);
  }
}

// Make updateSW available globally for the UpdateNotification component
(window as any).updateSW = updateSW;

// Initialize production services
initializeProductionServices();

// Initialize sync service
syncService.initialize();

// Initialize cache management on load
initializeCacheManagement();

// Initialize performance monitoring
initializePerformanceMonitoring();
console.log('Performance monitoring initialized');

// Cleanup on page unload
window.addEventListener('beforeunload', () => {
  syncService.cleanup();
});

// Handle quota exceeded errors
window.addEventListener('error', (event) => {
  if (event.error?.name === 'QuotaExceededError') {
    console.warn('Storage quota exceeded, cleaning up caches...');
    cacheService.cleanupCaches();
  }
});

// Handle online/offline events for cache optimization
window.addEventListener('online', () => {
  console.log('Back online, checking cache consistency...');
  // Optionally refresh critical caches when back online
});

window.addEventListener('offline', () => {
  console.log('Gone offline, cache-only mode active');
});

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <MaintenanceMode>
      <App />
    </MaintenanceMode>
  </React.StrictMode>,
)
